import { useMutation, useQuery, useQueryClient, QueryClient } from '@tanstack/react-query';
import { AnyAction, Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';

import { userSettingsService } from 'services';
import {
  PAGE_SIZE_SETTING_KEY,
  QueryContextFromKeys,
  QueryParamModel,
  TABLE_VIEWS_SETTING_KEY,
  UserModel,
  UserSettingsModel,
} from 'models';
import { RouteNames } from 'config/routeNames';
import { selectFilterParams } from 'store/common';
import { selectProfile, setProfileSettings } from 'store/profile';
import { REACT_QUERY_OPTIONS } from 'config/reactQueryConfig';

type userSettingsQueryContexts = QueryContextFromKeys<typeof userSettingsKeys>;

export const userSettingsKeys = {
  all: [{ scope: 'user-settings' }] as const,
  lists: () => [{ ...userSettingsKeys.all[0], entity: 'list' }] as const,
  userSettingsList: (params?: QueryParamModel | null) =>
    [{ ...userSettingsKeys.lists()[0], params }] as const,
  byId: (id: ID) => [{ ...userSettingsKeys.lists()[0], id }] as const,
  byScope: (scope: RouteNames) => [{ ...userSettingsKeys.lists()[0], scope }] as const,
};

const fetchUserSettingsList = async ({
  queryKey: [{ params: _params }],
}: userSettingsQueryContexts['userSettingsList']) => userSettingsService.getUserSettingsList();

export const useUserSettingsList = (params?: QueryParamModel | null) => {
  return useQuery({
    queryKey: userSettingsKeys.userSettingsList(params),
    queryFn: fetchUserSettingsList,
  });
};

const fetchUserSettingsById = async ({ queryKey: [{ id }] }: userSettingsQueryContexts['byId']) =>
  userSettingsService.getUserSettingsById(+id);

export const useUserSettingsById = (id: ID) =>
  useQuery({ queryKey: userSettingsKeys.byId(id), queryFn: fetchUserSettingsById });

const fetchUserSettingsByScope = async ({
  queryKey: [{ scope }],
}: userSettingsQueryContexts['byScope']) => userSettingsService.getUserSettingsByScope(scope);

export const useUserSettingsByScope = (scope: RouteNames) =>
  useQuery({ queryKey: userSettingsKeys.byScope(scope), queryFn: fetchUserSettingsByScope });

const isPageSizeSetting = (key?: string) => key === PAGE_SIZE_SETTING_KEY || key === undefined;

const isTableViewsSetting = (key?: string) => key === TABLE_VIEWS_SETTING_KEY;

const patchUserSettingsListCache = (
  queryClient: QueryClient,
  updater: (settings: UserSettingsModel[]) => UserSettingsModel[]
) => {
  queryClient.setQueriesData<UserSettingsModel[]>({ queryKey: userSettingsKeys.lists() }, (old) =>
    old ? updater(old) : old
  );
};

export const useDeleteUserSettings = () => {
  const filterParams = useSelector(selectFilterParams);
  const userProfile = useSelector(selectProfile);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload: ID | { id: ID; key?: string }) => {
      const id = typeof payload === 'object' ? payload.id : payload;
      return userSettingsService.deleteUserSettings(id);
    },
    meta: {
      [REACT_QUERY_OPTIONS.SKIP_DEFAULT_SUCCESS_CALLBACK]: true,
    },
    onSuccess: (_data, payload) => {
      const key = typeof payload === 'object' ? payload.key : PAGE_SIZE_SETTING_KEY;
      const id = typeof payload === 'object' ? payload.id : payload;

      if (isTableViewsSetting(key)) {
        patchUserSettingsListCache(queryClient, (settings) =>
          settings.filter((item) => item.id !== Number(id))
        );
      }

      queryClient.invalidateQueries({ queryKey: userSettingsKeys.lists() });
      if (isPageSizeSetting(key)) {
        deleteLocalSettings(userProfile, filterParams, dispatch);
      }
    },
  });
};

export const useEditUserSettings = () => {
  const filterParams = useSelector(selectFilterParams);
  const userProfile = useSelector(selectProfile);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, data }: { id: ID; data: Partial<UserSettingsModel> }) =>
      userSettingsService.editUserSettings(id, data),
    meta: {
      [REACT_QUERY_OPTIONS.SKIP_DEFAULT_SUCCESS_CALLBACK]: true,
    },
    onSuccess: (data, variables) => {
      if (isTableViewsSetting(variables.data.key ?? data?.key)) {
        patchUserSettingsListCache(queryClient, (settings) =>
          settings.map((item) =>
            item.id === Number(variables.id)
              ? {
                  ...item,
                  ...data,
                  value: variables.data.value ?? data?.value ?? item.value,
                }
              : item
          )
        );
      }

      queryClient.invalidateQueries({ queryKey: userSettingsKeys.lists() });
      if (
        data &&
        isPageSizeSetting(variables.data.key ?? data.key) &&
        typeof data.value === 'number'
      ) {
        updateLocalSettings(userProfile, filterParams, dispatch, data.value);
      }
    },
  });
};

export const useCreateUserSettings = () => {
  const queryClient = useQueryClient();
  const filterParams = useSelector(selectFilterParams);
  const userProfile = useSelector(selectProfile);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (data: Partial<UserSettingsModel>) => userSettingsService.createUserSettings(data),
    meta: {
      [REACT_QUERY_OPTIONS.SKIP_DEFAULT_SUCCESS_CALLBACK]: true,
    },
    onSuccess: (data, variables) => {
      if (data && isTableViewsSetting(variables.key ?? data.key)) {
        patchUserSettingsListCache(queryClient, (settings) => [...settings, data]);
      }

      queryClient.invalidateQueries({ queryKey: userSettingsKeys.lists() });
      if (data && isPageSizeSetting(variables.key ?? data.key) && typeof data.value === 'number') {
        updateLocalSettings(userProfile, filterParams, dispatch, data.value);
      }
    },
  });
};

const deleteLocalSettings = (
  userProfile: UserModel | null,
  filterParams: QueryParamModel,
  dispatch: Dispatch<AnyAction>
) => {
  const settings = { ...userProfile?.settings };
  delete settings[`${filterParams.from}`];
  dispatch(setProfileSettings(settings));
};

const updateLocalSettings = (
  userProfile: UserModel | null,
  filterParams: QueryParamModel,
  dispatch: Dispatch<AnyAction>,
  pageSize: number
) => {
  const newPageSize = { [`${filterParams.from}`]: { pageSize } };
  const newSettings = { ...userProfile?.settings, ...newPageSize };
  dispatch(setProfileSettings(newSettings));
};
